/**
 * planBathroomStaging — pure placement for a staged bathroom.
 *
 * Pure: no I/O, no Firestore, no React, no THREE. The caller decides whether
 * the result is previewed or persisted.
 *
 * The programme is spent in PRIORITY ORDER against a hard cap, because the
 * failure mode the staging literature warns about is too many props, not too
 * few: "when buyers notice the staging itself, it is usually a sign the
 * staging is not working". So a cramped bathroom gets the props that define a
 * staged one — the towel programme, a tray, something living — and drops the
 * garnish, rather than cramming nine objects onto a 24-inch vanity.
 *
 * Order of placement is load-bearing in two places:
 *   - the tray is placed BEFORE the props that stand on it, because it then
 *     becomes their host surface;
 *   - everything already placed is fed forward as an obstacle, because the
 *     free-zone measuring reads `room.objects`, which contains nothing planned
 *     in this run. Without that, two props claim the same stretch of vanity.
 */

const { extractWallEndpoints } = require('../../bathroom-analyzer/extraction');
const { isBasinLike } = require('./surfaces');
const { footprintFor } = require('../../geometry/objectFootprint');
const { resolveRoomKind } = require('../../rooms/roomKind');
const { getFloorY } = require('../../dorm-styling/dormDecorGeometry');
const { objectsYOverlap } = require('../../geometry/verticalOverlap');
const {
  isFloorPointClear,
  doorWallIdSet,
} = require('../../dorm-styling/plan/shared');
const { stagingPropDimsM } = require('../propDims');
const { objectTypeOf } = require('../ownership');
const { createMinter } = require('./minter');
const { trimToBudget } = require('./budget');
const { stagingEntry } = require('./entry');
const { doorSwingZones, clearsDoorSwings } = require('./doorSwing');
const {
  findSurfaceHosts,
  placeOnSurface,
  placeNearOnSurface,
} = require('./surfaces');
const { rankWallsByFreeRun, hangOnWall, placeAgainstWall } = require('./walls');
const {
  wallAxesFor,
  inwardFacingAt,
  getFloorPolygon,
  pointToSegmentDistanceXZ,
} = require('../../dorm-styling/dormDecorGeometry');
const {
  MAX_PROPS_PER_BATHROOM,
  HANGING_TOWEL_FITTINGS,
  MAX_HANGING_TOWEL_FITTINGS,
  programFor,
  AVOID_HANGING_OVER,
  WET_ZONE_TYPES,
  BATHING_FIXTURE_TYPES,
  ART_PREFERS_OVER,
  ART_STACK_GAP_M,
  FLOOR_CLEARANCE_M,
  FLOOR_PROP_NEAR_ANCHOR_M,
  FIXTURE_SURFACE_HOSTS,
  TRAY_TYPES,
  surfaceTopY,
  FIXTURE_CLEARANCE_M,
  SURFACE_HOST_TYPES,
  HAND_TOWEL_ABOVE_COUNTER_M,
  TOWEL_NEAR_COUNTER_M,
  STAGING_WALL_MOUNT_HEIGHTS,
  STAGING_WALL_MOUNT_MEASURE_FROM,
  TP_BESIDE_TOILET_M,
  TP_NEAR_TOILET_M,
  TP_REFUSE_OVER,
  RING_NEAR_BASIN_M,
  MAX_TOWEL_BAR_HEIGHT_M,
  WET_WALL_CLEARANCE_M,
  DRAPE_CLEARANCE_M,
} = require('./constants');

/**
 * Stand ONE prop on a fixture that is not a work surface — a shower niche.
 *
 * Not routed through `findSurfaceHosts`/`placeOnSurface`: that returns one
 * best host for the whole room and packs a ROW along its local x, and a niche
 * wants neither.
 *
 * Facing comes from `inwardFacingAt`, NOT from the host's stored `rotation.y`.
 * All 6 corpus niches store the same yaw as their parent wall, and a wall's
 * stored yaw is ambiguous by pi — "268 of 290 dev rooms have walls that
 * disagree" — which is how 59% of dorm's wall decor ended up facing into the
 * wall. Position and rotation come from the same call, as `walls.js` does it.
 */
function placeOnFixtureSurface(room, host, type, make, endpointsById) {
  const dims = stagingPropDimsM(type);
  const hd = host?.dimensions;
  if (!dims || !hd || !host.center) return null;

  // It has to FIT the recess, and the prop is placed CENTRED, so it needs the
  // clearance on BOTH sides -- not once, as a first version had it, which let a
  // prop 1 in narrower than the niche count as fitting with 0.5 in a side.
  const clear = 2 * FIXTURE_CLEARANCE_M;
  if (dims.z + clear > (hd.z || 0)) return null;
  if (dims.x + clear > (hd.x || 0)) return null;
  const floorY = surfaceTopY(host);
  if (floorY == null) return null;
  const headroom = host.center.y + (hd.y || 0) / 2 - floorY;
  if (dims.y > headroom) return null;

  let rotationY = host.rotation?.y || 0;
  const wallId = (host.parentWallIds || [])[0];
  const wall = (room.walls || []).find((w) => w.id === wallId);
  const resolved = wall ? wallAxesFor(wall, endpointsById) : null;
  if (resolved?.axes) {
    const axes = resolved.axes;
    const mid = {
      x: axes.start.x + axes.wallDir.x * (axes.wallLen / 2),
      z: axes.start.z + axes.wallDir.z * (axes.wallLen / 2),
    };
    const facing = inwardFacingAt(mid, axes, getFloorPolygon(room));
    if (facing) rotationY = facing.rotationY;
  }

  return make(type, {
    center: { x: host.center.x, y: floorY + dims.y / 2, z: host.center.z },
    dimensions: dims,
    rotation: { x: 0, y: rotationY, z: 0 },
    host,
  });
}

/**
 * Why a wall piece found nowhere to go. One line per piece, because "no wall"
 * means something different for each: the ring needs the basin's wall, the
 * holder the toilet's, and the towels and art need any wall with a clear run
 * that is not in front of the shower.
 */
const WALL_DROP_REASON = {
  'staging-towel-ring': 'no-wall-beside-the-basin',
  'staging-toilet-paper': 'no-wall-beside-the-toilet',
};

/**
 * What the bar slot could not find, once a ring has taken the hand towel and
 * the slot is therefore the BODY towel. Distinct from the generic reason,
 * because the hand towel was never attempted — reporting "no wall free for a
 * towel" would read as though the short one had been tried and refused too.
 */
const NO_BODY_TOWEL = 'no-wall-for-a-body-towel';
const HAND_TOWEL_ONLY = 'hand-towel-only-without-a-bath';

function planBathroomStaging(room, style = {}, opts = {}) {
  // A guard, not a silent bug-hider: the live-preview caller can invoke this
  // before room data has loaded.
  if (!room) return [];

  const walls = room.walls || [];
  const endpoints = extractWallEndpoints(room) || [];
  const endpointsById = new Map(endpoints.map((e) => [e.id, e]));

  const program = programFor(resolveRoomKind(room));
  const entries = [];
  const used = new Set();
  const { make, mint, curation, drop, wantedOf } = createMinter(
    room,
    style,
    opts,
    program
  );

  // ---- 1. Wall pieces. The towel programme first: it is the single strongest
  // signal that a bathroom has been styled rather than tidied.
  const wallCandidates = rankWallsByFreeRun(room, walls, endpointsById);
  // Types this room has ALREADY searched every wall for and failed; see the
  // `attempts` note below.
  const triedAndFailed = new Set();
  // Anything the room is DRAWN with that is a bath or a shower, or a part of
  // one — not only the wet-zone footprints the scan tagged.
  const roomHasBathing = (room.objects || []).some((o) =>
    BATHING_FIXTURE_TYPES.has(objectTypeOf(o))
  );
  for (const prop of wantedOf('wall')) {
    // Two hung towels is the whole story in a staged bathroom; see
    // MAX_HANGING_TOWEL_FITTINGS. Checked here rather than inside the wall
    // search because it is a CURATION rule about the room, not a question
    // about any wall.
    if (
      HANGING_TOWEL_FITTINGS.has(prop.type) &&
      entries.filter((e) => HANGING_TOWEL_FITTINGS.has(e.type)).length >=
        MAX_HANGING_TOWEL_FITTINGS
    ) {
      drop(prop.type, 'the-room-already-has-two-towels');
      continue;
    }
    // Guarded like every other list: a counter record with no box would
    // otherwise reach `objectGapToWall` through the ring's `requireWallOf`
    // and throw, costing the room every prop for one malformed scan object.
    const counters = (room.objects || []).filter(
      (o) =>
        SURFACE_HOST_TYPES.includes(objectTypeOf(o)) && o.center && o.dimensions
    );
    const toilets = (room.objects || []).filter(
      (o) => objectTypeOf(o) === 'toilet' && o.center && o.dimensions
    );
    // One table, not a ternary chain. `STAGING_WALL_MOUNT_HEIGHTS` is the
    // same one `objectTypeConfigs` hands the manual-placement path, which is
    // the whole reason it exists — and a chain had already grown to three
    // arms for four pieces. Looked up by the PROGRAMME's type, so the bath
    // towel substituted for the bar inherits the bar's height.
    //
    // A missing entry is REPORTED, not defaulted: silently hanging a new prop
    // at towel height is the kind of wrong that nobody notices for months.
    if (STAGING_WALL_MOUNT_HEIGHTS[prop.type] == null) {
      drop(prop.type, 'no-mount-height-declared');
      continue;
    }
    // Is a ring hung HERE still this basin's towel ring? Handed to
    // `hangOnWall` as its `accept`, so a wall that fails it is passed over
    // and the next candidate is tried — and, critically, is never CLAIMED.
    // Checked on the returned entry instead and the refusal burned the wall:
    // `hangOnWall` marks a wall used the moment it hangs something, the
    // caller cannot undo that, and the bar was then refused the only wall the
    // room had. One corpus bathroom lost its towel entirely.
    const ringBasins = (room.objects || []).filter(
      (o) => isBasinLike(o) && o.center
    );
    // NO basin means refused, not waved through: a hand-towel ring exists to
    // sit beside a basin, and with none in the room there is nothing for it to
    // be beside. The check this replaced expressed that as a reach of
    // Infinity; `!ringBasins.length ||` would invert it, and does so quietly
    // — the room keeps its bar, so the only visible symptom is a stray ring
    // on the wall of a bathroom that has no sink.
    const ringIsWithinReach = (centre) =>
      ringBasins.some(
        (b) =>
          Math.hypot(centre.x - b.center.x, centre.z - b.center.z) <=
          RING_NEAR_BASIN_M
      );
    // Is a roll holder HERE still this toilet's? Measured to the toilet's
    // FOOTPRINT, not its centre: a toilet is about 27 in deep, so a
    // centre-measured reach would charge the holder for the length of the pan
    // it is beside. Same shape of gate as the ring's, and for the same
    // reason — the slide target is clamped into the clear run, so a
    // qualifying wall can still put the holder across the room.
    const tpIsWithinReach = (centre) =>
      toilets.some((o) => {
        const f = footprintFor(
          o.center,
          o.dimensions.x,
          o.dimensions.z,
          o.rotation?.y || 0
        );
        if (!f) return false;
        let best = Infinity;
        for (let i = 0; i < f.length; i++) {
          best = Math.min(
            best,
            pointToSegmentDistanceXZ(centre, f[i], f[(i + 1) % f.length])
          );
        }
        return best <= TP_NEAR_TOILET_M;
      });
    const liftToCounter = {
      objects: counters,
      withinM: TOWEL_NEAR_COUNTER_M,
      aboveM: HAND_TOWEL_ABOVE_COUNTER_M,
      // The HEM, not just the mount: an 18 in drape hung at counter + 20 in
      // clears a counter by 2 in, and by 0.7 in on a tall one, which reads as
      // a towel lying on the worktop.
      hemClearanceM: DRAPE_CLEARANCE_M,
      maxM: MAX_TOWEL_BAR_HEIGHT_M,
    };
    // What each wall piece asks of a wall. One branch per piece rather than
    // a ternary chain: they are four different rules, not four values.
    const optionsFor = (type) => {
      if (type === 'staging-towel-bath') {
        // A bath towel only hangs where a bath towel BELONGS. Everything the
        // hand-towel bar merely demotes away from, this REFUSES: 26 in of
        // terry hung over a cistern passes through it, and over a vanity it
        // is in front of the mirror. A room with nowhere good keeps its hand
        // towel instead -- that is the second attempt below, not a failure.
        return {
          forbidOverTypes: WET_ZONE_TYPES,
          forbidClearanceM: WET_WALL_CLEARANCE_M,
          refuseOverTypes: AVOID_HANGING_OVER,
          measureFrom: STAGING_WALL_MOUNT_MEASURE_FROM[type],
          // No lift. `liftToCounter` exists to stop a drape lying on the
          // counter it hangs over -- but this towel REFUSES a wall it would
          // hang over a counter on, so there is nothing left to lift above,
          // and applying it anyway pushed one corpus towel to a 60 in bar
          // (the cap) because a vanity stood within 36 in on another wall.
        };
      }
      if (type === 'staging-towel-ring') {
        // A hand towel on a ring belongs BESIDE the basin — the one wall the
        // bath towel deliberately avoids. So it is the bar's mirror image: it
        // PREFERS the basin's wall rather than demoting away from it, and it
        // is refused the wet zone outright, because a hand towel in a shower
        // is not a hand towel.
        return {
          forbidOverTypes: WET_ZONE_TYPES,
          forbidClearanceM: WET_WALL_CLEARANCE_M,
          // NO `requireWallOf`. It asked for the counter's wall until
          // 2026-10-11, then the basin's or its counter's — and both were
          // proxies for the rule that actually matters, which is the reach
          // gate below.
          //
          // `attributeToWalls` gives an object its single NEAREST wall, so any
          // form of this pins the ring to the wall the vanity backs onto and
          // cannot see the short RETURN wall at the end of a vanity — which is
          // often the best spot in the room for a hand towel: it is inches
          // from the basin, it is the wall nobody else wants, and a ring fits
          // where a bar would not. ("i also think you can place a hand towel
          // ring the right wall adjacent to the vanity sinks. its a smaller
          // wall but the towel ring definitely would fit on it and it is very
          // close to the sink" — 2026-10-11, on `704C9E88`.) It also cost a
          // pedestal basin its ring for want of a worktop: 10 of the 46
          // corpus bathrooms that had no ring had a basin and no counter.
          //
          // What keeps this honest is `RING_NEAR_BASIN_M` — arm's reach of the
          // basin, checked per candidate before the wall is claimed — plus
          // `slideToward`, which pulls the ring as close alongside the basin
          // as the run allows.
          slideToward: (room.objects || []).filter((o) => isBasinLike(o)),
          measureFrom: STAGING_WALL_MOUNT_MEASURE_FROM[type],
          liftOver: liftToCounter,
          // Arm's reach of a basin, checked per CANDIDATE. The slide target
          // is clamped into the wall's clear run, so a wall that qualifies
          // can still put the ring 88 in from the sink — and a fitting that
          // far away is not that basin's towel ring.
          accept: (fields) => ringIsWithinReach(fields.center),
        };
      }
      if (type === 'staging-toilet-paper') {
        // Within reach of the toilet, beside it rather than over it — on
        // WHICHEVER wall can offer that.
        //
        // Deliberately no `requireWallOf: toilets`. That was the first rule
        // and it is a proxy for this one: `attributeToWalls` gives an object
        // its single NEAREST wall, so requiring it pins the holder to the wall
        // the pan backs onto — which is both the wall a towel has usually
        // claimed already and the one where there is least room beside the
        // pan. Measured, it refused the holder 66 times for a wall already in
        // use and 36 times for not being the toilet's. The real rule is the
        // reach gate below, and lifting the proxy also admits the SIDE wall of
        // a toilet alcove, which is where a holder ideally goes.
        //
        // `slideBeside` then puts it just clear of the pan along whichever
        // wall it lands on — necessary because the free run is measured in the
        // hanging band, whose floor is 52 in, so a ~30 in toilet is invisible
        // to it and the run reported as clear includes the pan itself.
        return {
          forbidOverTypes: WET_ZONE_TYPES,
          forbidClearanceM: WET_WALL_CLEARANCE_M,
          refuseOverTypes: TP_REFUSE_OVER,
          slideBeside: { objects: toilets, gapM: TP_BESIDE_TOILET_M },
          // It may share a wall with a towel. At 26 in it finishes a clear
          // foot below a bar at 48 in, and the towels run first — so in a
          // small bathroom, where the toilet's wall is also the only wall a
          // towel could take, refusing a used wall is refusing the holder.
          shareWallClearOf: entries.filter((e) => e.parentWallIds?.length),
          shareWallGapM: ART_STACK_GAP_M,
          accept: (fields) => tpIsWithinReach(fields.center),
        };
      }
      if (type === 'staging-towel-hooks') {
        // The BATH towel's rule, because that is what hooks are an
        // alternative to — "the full size body towel bar, or the body towel
        // hook". So everything the hand-towel bar merely demotes away from,
        // this REFUSES.
        //
        // Demoting was the first version and it hung the hooks on the wall
        // directly behind the cistern in `DA6C0393`: the towels were lost
        // behind the tank, which read as the prop not rendering at all. A
        // body towel over the toilet is the wrong place for it whatever the
        // height, and over a vanity it is where the mirror goes.
        //
        // No counter lift, unlike the hand-towel bar: at 66 in the hooks are
        // already a clear foot above any worktop and the drape finishes at
        // 51 in, so there is nothing to lift over.
        return {
          refuseOverTypes: AVOID_HANGING_OVER,
          forbidOverTypes: WET_ZONE_TYPES,
          forbidClearanceM: WET_WALL_CLEARANCE_M,
          measureFrom: STAGING_WALL_MOUNT_MEASURE_FROM[type],
        };
      }
      if (type === 'staging-art') {
        // Never inside a tub or shower surround; over the toilet for choice;
        // and free to share the towel's wall by hanging above the rod.
        return {
          forbidOverTypes: WET_ZONE_TYPES,
          preferOverTypes: ART_PREFERS_OVER,
          stageAbove: entries.filter((e) => e.parentWallIds?.length),
          stageAboveGapM: ART_STACK_GAP_M,
        };
      }
      // A towel over the toilet or the vanity is not wrong enough to drop, it
      // hangs FROM its bar rather than straddling it, and beside a vanity it
      // is a HAND towel, mounted off the counter.
      return {
        avoidOverTypes: AVOID_HANGING_OVER,
        // A tub REFUSES the wall, it does not merely demote it -- and it
        // claims the whole wall SECTION in front of it, not just the sliver
        // its own footprint covers.
        forbidOverTypes: WET_ZONE_TYPES,
        forbidClearanceM: WET_WALL_CLEARANCE_M,
        measureFrom: STAGING_WALL_MOUNT_MEASURE_FROM[type],
        liftOver: liftToCounter,
      };
    };

    // Once the RING has placed, this slot is the BODY towel — a full-size bar,
    // or hooks if a bar will not fit. Never a second hand towel.
    //
    // `staging-towel-hanging` is sized as a hand towel on purpose: when it is
    // the room's only towel that is the job it does, mounted clear of the
    // counter. Once a ring is beside the basin the room already HAS its hand
    // towel, and a second one is the thing you dry off with missing. ("if we
    // have the handtowel ring, then we should use the full size body towel
    // bar, or the body towel hook" — 2026-10-11.) The staging literature says
    // the same from the other side: a bath towel squeezed into a hand-towel
    // ring makes a small room feel smaller, so the ring takes the hand towel
    // and the body towel needs a fitting of its own.
    //
    // This used to fall back to `prop.type` — the hand towel — on the
    // reasoning that a room where the longer towel fits nowhere keeps the
    // short one rather than losing its towel. That reasoning was written as
    // if the bar were the room's only towel, and it is exactly wrong here: the
    // ring is already up, so the fallback added a SECOND hand towel and no
    // body towel, which is what `DA6C0393` showed. Hooks are 17 in against the
    // bar's 24, so they genuinely fit walls it cannot; if neither does, the
    // room keeps the ring alone.
    // An attempt is a TYPE plus any extra options, because the ring's two
    // attempts differ only in how strict they are about the wall.
    const ringWalls = [
      ...(room.objects || []).filter(
        (o) => isBasinLike(o) && o.center && o.dimensions
      ),
      ...counters,
    ];
    // Candidates for the RING, nearest the basin first.
    //
    // `rankWallsByFreeRun` sorts by widest free run, which is right for a
    // piece that just needs somewhere to go and wrong for one whose whole
    // point is to be beside something: a wide wall across the room beats the
    // narrow one next to the sink, and the reach gate is happy with either.
    // On `704C9E88` that put the ring on the partition 35 in away, across the
    // room from the basin it serves. ("we place the ring hand towel adjacent
    // to sink not on a wall across sink" — 2026-10-13.)
    const nearestBasinFirst = (candidates) => {
      const basins = (room.objects || []).filter(
        (o) => isBasinLike(o) && o.center
      );
      if (!basins.length) return candidates;
      // Distance to the candidate's RUN, not to its whole wall: a wall split
      // by a tall unit offers a run at each end, and the two tie on the wall
      // while only one of them is beside the basin.
      const runSegment = (cand) => {
        const { start, wallDir } = cand.axes;
        const at = (t) => ({
          x: start.x + wallDir.x * t,
          z: start.z + wallDir.z * t,
        });
        return [at(cand.span.start), at(cand.span.end)];
      };
      const reach = (cand) => {
        const [a, b] = runSegment(cand);
        return Math.min(
          ...basins.map((o) => pointToSegmentDistanceXZ(o.center, a, b))
        );
      };
      return [...candidates].sort((a, b) => reach(a) - reach(b));
    };

    // The hooks: the PAIR first, then ONE hook with one towel where no wall
    // has room for two ("they don't have to spawn both hooks. can just spawn
    // one" — 2026-10-16). Same type and height; the narrower box is the
    // whole difference, and the renderer draws one hook or two by it.
    const hookAttempts = [
      { type: 'staging-towel-hooks' },
      {
        type: 'staging-towel-hooks',
        extra: { dims: stagingPropDimsM('staging-towel-hooks', 'single') },
      },
    ];
    // Once the ring is up, the body-towel slot tries the hooks; the hooks'
    // own programme line used to run the identical search a second time and
    // report a second drop for the one missing body towel.
    if (triedAndFailed.has(prop.type)) continue;
    // A BODY towel — the bath towel or the hooks — only where there is a bath
    // or a shower to dry off from. A powder room gets its hand towel (the
    // ring, else the small bar) and nothing else: "a room with no tub or
    // shower should only get a hand towel with no bath towel or hooks"
    // (2026-10-17). Reported as a drop so the ledger says why, not a failure.
    const ringUp = entries.some((e) => e.type === 'staging-towel-ring');
    if (
      (prop.type === 'staging-towel-hooks' ||
        (prop.type === 'staging-towel-hanging' && ringUp)) &&
      !roomHasBathing
    ) {
      drop(prop.type, HAND_TOWEL_ONLY);
      continue;
    }
    const attempts =
      prop.type === 'staging-towel-hanging' && ringUp
        ? [{ type: 'staging-towel-bath' }, ...hookAttempts]
        : prop.type === 'staging-towel-hooks'
          ? hookAttempts
          : prop.type === 'staging-towel-ring'
            ? [
                // The basin's own wall, or its counter's — by far the best spot
                // and the one the ring has always taken.
                { type: prop.type, extra: { requireWallOf: ringWalls } },
                // Failing that, ANY wall that still puts the ring within arm's
                // reach of the basin. `attributeToWalls` gives an object its
                // single NEAREST wall, so the strict pass cannot see the short
                // RETURN wall at the end of a vanity — often the best spot in
                // the room for a hand towel: inches from the basin, wanted by
                // nothing else, and a ring fits where a bar would not. ("its a
                // smaller wall but the towel ring definitely would fit on it
                // and it is very close to the sink" — 2026-10-11.)
                //
                // Two passes and not one relaxed rule: dropping the strict pass
                // altogether moved `DA6C0393`'s ring off the vanity wall onto
                // the toilet's, 35 in from the basin instead of 28, because
                // candidates arrive widest-run-first and the reach gate alone
                // was happy with it. Preference has to come before permission.
                { type: prop.type },
              ]
            : [{ type: prop.type }];

    let entry = null;
    for (const attempt of attempts) {
      const attemptType = attempt.type;
      // Per ATTEMPT, not per programme line. The bath-towel substitution
      // happens to share the bar's 48 in, so keying this on `prop.type` worked
      // by coincidence — hooks hang at 66 in and would have gone up at 48,
      // which is a bath-towel bar's height with a hook's geometry on it.
      const height = STAGING_WALL_MOUNT_HEIGHTS[attemptType];
      if (height == null) continue;
      entry = hangOnWall(
        room,
        attemptType === 'staging-towel-ring'
          ? nearestBasinFirst(wallCandidates)
          : wallCandidates,
        attemptType,
        height,
        (fields) => mint(prop, attemptType, fields),
        used,
        { ...optionsFor(attemptType), ...(attempt.extra || {}) }
      );
      if (entry) break;
    }
    if (entry) entries.push(entry);
    else {
      for (const a of attempts) triedAndFailed.add(a.type);
      // EVERY wall prop reports its failure, not just the ring. `onDrop`
      // promises everything the room wanted and could not have, and until
      // 2026-10-11 only the ring kept that promise here -- which is why a
      // toilet roll holder that placed in 5 of 55 rooms with a toilet in them
      // was reporting nothing at all to say so.
      if (attempts[0].type === 'staging-towel-bath')
        drop(prop.type, NO_BODY_TOWEL);
      else
        drop(prop.type, WALL_DROP_REASON[prop.type] || 'no-wall-free-for-it');
    }
  }

  // The surface the vignette goes on. `findSurfaceHosts` ranks them — a
  // counter carrying a basin first — but the best-ranked one may have no room
  // once its basin is voided, and a 29 in vanity around a 20 in bowl has about
  // 1.5 in a side. Fall through to the next rather than stage nothing: the
  // ranking says which surface we'd PREFER, not which one can take anything.
  const hosts = findSurfaceHosts(room);
  const firstWish = program.find((p) => p.where === 'surface' && !p.near);
  const firstDims = firstWish ? stagingPropDimsM(firstWish.type) : null;
  const host =
    hosts.find((candidate) => {
      if (!firstDims) return true;
      const probe = placeOnSurface(
        candidate,
        [firstWish],
        [...(room.objects || [])],
        (wish, fields) => ({ type: wish.type, ...fields })
      );
      return probe.placed.length > 0;
    }) ||
    hosts[0] ||
    null;
  // Every surface stage below is guarded by `if (host && ...)`, so with no
  // countertop in the room they simply never run -- and `onDrop` promises
  // "everything this room wanted and could not have". Report it here, or the
  // one case the caller most needs to hear about is the one case that is
  // silent.
  if (!host) {
    for (const prop of [...wantedOf('surface'), ...wantedOf('tray')]) {
      drop(prop.type, 'no-surface-host-in-room');
    }
  }

  // ---- 2. Props with a POSITION to hold: soap by the sink, which is where a
  // bar of soap actually lives and where a buyer looks for it.
  //
  // Before the free row, not after. A prop that must sit somewhere specific
  // loses every race against one that will go anywhere: run the row first and
  // it fills the counter, leaving no gap within reach of the sink, and the
  // soap is dropped despite being higher priority.
  if (host) {
    for (const prop of wantedOf('surface').filter((p) => p.near === 'sink')) {
      // The SAME predicate the void pass uses. Two predicates here is how the
      // soap ends up anchored in a bowl that was never voided.
      const sink = (room.objects || []).find(
        (o) => o?.center && isBasinLike(o)
      );
      // No sink found is not a reason to drop the soap — fall back to the
      // host's own centre, which on a vanity is close enough.
      const anchorPoint = sink?.center || host.center;
      const placed = placeNearOnSurface(
        host,
        prop,
        { x: anchorPoint.x, z: anchorPoint.z },
        [...(room.objects || []), ...entries],
        (wish, fields) => mint(wish, wish.type, fields)
      );
      if (placed) entries.push(placed);
      else drop(prop.type, 'no-solid-surface-near-anchor');
    }
  }

  // ---- 3. The vanity top. One composed vignette on the roomiest surface,
  // rather than decor spread over every surface in the room.
  let tray = null;
  if (host) {
    // Props with a `near` hint are placed individually afterwards, so they fill
    // a gap the composed row left rather than pushing it around.
    const wishes = wantedOf('surface').filter((p) => !p.near);
    const { placed, rejected } = placeOnSurface(
      host,
      wishes,
      [...(room.objects || []), ...entries],
      (wish, fields) => mint(wish, wish.type, fields)
    );
    for (const entry of placed) {
      entries.push(entry);
      if (TRAY_TYPES.has(entry.type)) tray = entry;
    }
    for (const wish of rejected) drop(wish.type, 'surface-run-too-short');
  }

  // ---- 4. Props that stand ON the tray. The tray is simply another host, so
  // this is the same packer again — which is the whole reason a tray earns its
  // place: it corrals loose props into one object the eye reads as a group.
  if (!tray) {
    // Same trap one level down: the tray's contents are never ATTEMPTED when
    // no tray was placed, so "the programme never asked" and "its host was
    // never built" would be indistinguishable from outside.
    for (const prop of wantedOf('tray')) drop(prop.type, 'no-tray-to-stand-on');
  }
  if (tray) {
    const wishes = wantedOf('tray');
    const { placed, rejected } = placeOnSurface(
      tray,
      wishes,
      [...(room.objects || []), ...entries],
      (wish, fields) => mint(wish, wish.type, fields)
    );
    for (const entry of placed) {
      entries.push(entry);
    }
    for (const wish of rejected) drop(wish.type, 'tray-too-small');
  }

  // ---- 4b. Props standing on a FIXTURE rather than a work surface — the
  // amenity bottles in a shower niche, which is the one place in a bathroom
  // that exists to hold exactly this and nothing else.
  for (const prop of wantedOf('fixture-surface')) {
    const fixtureHost = (room.objects || []).find((o) =>
      FIXTURE_SURFACE_HOSTS.includes(objectTypeOf(o))
    );
    if (!fixtureHost) {
      drop(prop.type, 'no-fixture-surface-in-room');
      continue;
    }
    const placed = placeOnFixtureSurface(
      room,
      fixtureHost,
      prop.type,
      (type, fields) => mint(prop, type, fields),
      endpointsById
    );
    if (placed) entries.push(placed);
    else drop(prop.type, 'does-not-fit-the-fixture');
  }

  // ---- 5. Floor props, standing AGAINST a wall. A basket out in the middle of
  // a bathroom reads as something dropped rather than styled, so it goes under
  // whatever is already hung — the towel rail or the art — and never free in
  // the room.
  const hungWallIds = new Set(
    entries.flatMap((e) => e.parentWallIds || []).filter(Boolean)
  );
  // Where a towel basket actually belongs: the drying zone beside the tub or
  // shower first, the vanity second. Nobody puts one by the door.
  const anchorObj =
    (room.objects || []).find((o) => WET_ZONE_TYPES.has(objectTypeOf(o))) ||
    hosts[0];
  const anchor = anchorObj?.center
    ? { x: anchorObj.center.x, z: anchorObj.center.z }
    : null;
  // Its FOOTPRINT, so "beside the tub" is measured from the tub's edge rather
  // than from a point inside it.
  const anchorFootprint =
    anchorObj?.center && anchorObj?.dimensions
      ? footprintFor(
          anchorObj.center,
          anchorObj.dimensions.x,
          anchorObj.dimensions.z,
          anchorObj.rotation?.y || 0
        )
      : null;
  const doorWallIds = doorWallIdSet(room, walls, endpointsById);
  for (const prop of wantedOf('against-wall')) {
    const dims = stagingPropDimsM(prop.type);
    if (!dims) continue;
    // Floor-level obstacles only. `isFloorPointClear` tests plan footprints
    // with no notion of height, so anything above the prop would otherwise
    // block the floor beneath it — which is exactly where a basket goes.
    //
    // Filtering only the planner's OWN wall-hung entries was not enough: a
    // ceiling light, a mirror, a wall sconce and a countertop are all in
    // `room.objects` and all blocked the basket. The band is the candidate's
    // own height interval, standing on the floor — not a fixed cut, because
    // the list also carries previously placed floor props, which must go on
    // seeing each other.
    //
    // `missingHeight: 'full'` is load-bearing: an object with no recorded
    // height is UNKNOWN, not absent, and treating it as a flat point would
    // silently stop it being an obstacle — including a user-marked immovable
    // closet, whose entire purpose is to be one.
    const floorY = getFloorY(room);
    const band = {
      center: { y: floorY + dims.y / 2 },
      dimensions: { y: dims.y },
    };
    const obstacles = [
      ...(room.objects || []),
      ...entries.filter((e) => !(e?.parentWallIds?.length > 0)),
    ].filter((o) => objectsYOverlap(band, o, { missingHeight: 'full' }));
    const clearDist = Math.max(dims.x, dims.z) / 2 + FLOOR_CLEARANCE_M;
    // Keep out of every door's swing, not merely off its wall
    // (`doorSwing.js`).
    const half = Math.max(dims.x, dims.z) / 2;
    const swings = doorSwingZones(room);
    const clearsDoors = (point) => clearsDoorSwings(point, swings, half);
    const placed = placeAgainstWall(room, wallCandidates, dims, {
      anchor,
      anchorFootprint,
      preferWallIds: hungWallIds,
      doorWallIds,
      isClear: (point) =>
        clearsDoors(point) && isFloorPointClear(point, obstacles, clearDist),
    });
    // Beside the fixture it belongs to, or not at all — the same standard the
    // towel ring is held to. A basket three feet from both the tub and the
    // vanity reads as dropped, not as styling.
    let tooFar = false;
    if (placed) {
      const anchors = [
        ...(anchorObj ? [anchorObj] : []),
        ...(room.objects || []).filter((o) =>
          SURFACE_HOST_TYPES.includes(objectTypeOf(o))
        ),
      ].filter((o) => o?.center && o?.dimensions);
      if (anchors.length) {
        const reach = Math.min(
          ...anchors.map((o) => {
            const f = footprintFor(
              o.center,
              o.dimensions.x,
              o.dimensions.z,
              o.rotation?.y || 0
            );
            if (!f) return Infinity;
            let best = Infinity;
            for (let i = 0; i < f.length; i++) {
              best = Math.min(
                best,
                pointToSegmentDistanceXZ(
                  placed.center,
                  f[i],
                  f[(i + 1) % f.length]
                )
              );
            }
            return best;
          })
        );
        tooFar = reach > FLOOR_PROP_NEAR_ANCHOR_M;
      }
    }
    if (placed && tooFar) drop(prop.type, 'no-clear-floor-beside-a-fixture');
    if (!placed) drop(prop.type, 'no-clear-floor-against-a-wall');
    if (placed && !tooFar) {
      const { againstWallId, ...fields } = placed;
      entries.push(mint(prop, prop.type, fields));
    }
  }

  return trimToBudget(entries, curation, drop, {
    maxProps: MAX_PROPS_PER_BATHROOM,
  });
}

module.exports = { planBathroomStaging, trimToBudget };
